import { EnumButtonName, EnumDialogName } from '@calm/evexia-analytics';
import { DS_SHAPE_SPACING, DS_SPACING_UNITS } from '@calm/evexia-design-tokens';
import { SuccessCheckAnimation } from '@rhg/components/animations/success-check-animation';
import { WisdomAnimation } from '@rhg/components/animations/wisdom-animation';
import { useProgramActions } from '@rhg/hooks/use-program-actions';
import { useProvidedProgramContext } from '@rhg/hooks/use-provided-program-context';
import programMessages from '@rhg/lang/messages/program';
import {
    Divider,
    List,
    ListItem,
    ListItemAvatar,
    ListItemText,
    Stack,
    Theme,
    Typography,
    useMediaQuery,
    useTheme,
} from '@rhg/libs/material';
import {
    HeartOutline,
    InfoOutlineThin,
    MentalHealthScreeningOutline,
} from '@rhg/libs/material/icons';
import SimpleDialog, {
    styleSimpleDialogButton,
} from '@rhg/libs/material/simple-dialog';
import { useId, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { ProgramNextButton } from '../next-button';

export interface GetStartedDialogProps {
    isOpen: boolean;
    justJoined: boolean;
    onClose: () => void;
}

const stylePaper = {
    background: (theme: Theme) => theme.palette.gradient.backgroundPrimary,
};

const styleScrollableContainer = {
    overflowY: 'auto',
};

const styleTitle = { mt: 2 };

const styleDivider = {
    width: '100%',
    mt: DS_SPACING_UNITS.SECTION,
};

const styleList = {
    background: 'transparent',
};

const styleSection = {
    mt: 4,
};

const styleSectionIcon = {
    height: DS_SHAPE_SPACING.BASE * 9,
    width: DS_SHAPE_SPACING.BASE * 9,
    color: 'primary.main',
};

const GetStartedDialog = ({
    isOpen,
    justJoined,
    onClose,
}: GetStartedDialogProps) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const { program } = useProvidedProgramContext();
    const { formatMessage } = useIntl();
    const { handleNextButton } = useProgramActions();
    const id = useId();

    const sections = useMemo(() => {
        return [
            {
                title: formatMessage(programMessages['program.summary_title']),
                body: program?.description,
                icon: <InfoOutlineThin sx={styleSectionIcon} />,
            },
            {
                title: formatMessage(
                    programMessages['program.favorite_sessions_title']
                ),
                body: formatMessage(
                    programMessages['program.favorite_sessions_body']
                ),
                icon: <HeartOutline sx={styleSectionIcon} />,
            },
            {
                title: formatMessage(
                    programMessages['program.check_ins_title']
                ),
                body: formatMessage(programMessages['program.check_ins_body']),
                icon: <MentalHealthScreeningOutline sx={styleSectionIcon} />,
            },
        ];
    }, [program?.description, formatMessage]);

    return (
        <SimpleDialog
            isOpen={isOpen}
            onClose={onClose}
            fullScreen
            trackDialog={EnumDialogName.HowToGetStarted}
            PaperProps={{
                sx: stylePaper,
            }}
            icon={justJoined ? <SuccessCheckAnimation /> : <WisdomAnimation />}
            iconColor="info"
            noIconPadding
            aria-labelledby={id}
            buttons={[
                {
                    rawComponent: (
                        <ProgramNextButton
                            key={0}
                            onClick={handleNextButton(() => onClose())}
                            trackButton={EnumButtonName.HowToGetStartedNext}
                            sx={styleSimpleDialogButton}
                        />
                    ),
                },
            ]}
            content={
                <Stack
                    alignItems="center"
                    sx={styleScrollableContainer}
                    tabIndex={0}
                >
                    {justJoined && (
                        <Typography
                            variant="subtitle1"
                            align="center"
                            mb={2}
                            sx={styleTitle}
                        >
                            {formatMessage(
                                programMessages['program.program_joined']
                            )}
                        </Typography>
                    )}
                    <Typography
                        variant={isMobile ? 'h5' : 'h4'}
                        component="h2"
                        align="center"
                        id={id}
                    >
                        {formatMessage(
                            programMessages['program.how_to_get_started_with'],
                            {
                                title: program?.title,
                            }
                        )}
                    </Typography>
                    <Divider sx={styleDivider} />
                    <List sx={styleList}>
                        {sections.map((section) => (
                            <ListItem key={section.title} sx={styleSection}>
                                <ListItemAvatar>{section.icon}</ListItemAvatar>
                                <ListItemText
                                    primary={section.title}
                                    primaryTypographyProps={{
                                        variant: 'h6',
                                        component: 'span',
                                    }}
                                    secondary={section.body}
                                    secondaryTypographyProps={{
                                        variant: 'subtitle2',
                                        component: 'p',
                                    }}
                                ></ListItemText>
                            </ListItem>
                        ))}
                    </List>
                </Stack>
            }
        />
    );
};

export { GetStartedDialog };
